import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { PIPING_OPTIONS } from '@/components/OptionsStep';
import { FOAM_DENSITIES } from '@/components/cushionConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useCustomizerFlow = ({
  cushionId,
  productName,
  initialDimensions,
  getDimensionsForApi,
  getCartExtras,
}) => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [productType, setProductType] = useState('cover_and_foam');
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const stepContentRef = useRef(null);

  const [cushion, setCushion] = useState(null);
  const [dimensionsCm, setDimensionsCm] = useState(initialDimensions);
  const [inputValues, setInputValues] = useState({});
  const [unit, setUnit] = useState('cm');

  const [selectedFabric, setSelectedFabric] = useState(null);
  const [selectedFoam, setSelectedFoam] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [priceInfo, setPriceInfo] = useState(null);

  const [selectedFabricRange, setSelectedFabricRange] = useState(null);
  const [selectedFabricColor, setSelectedFabricColor] = useState(null);
  const [fabricZoom, setFabricZoom] = useState(3);
  const [fabricRanges, setFabricRanges] = useState([]);
  const [pipingOption, setPipingOption] = useState('none');
  const [pipingFabricRange, setPipingFabricRange] = useState(null);
  const [pipingFabricColor, setPipingFabricColor] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const steps = useMemo(() => {
    const base = [
      { name: 'Product', description: 'Select type' },
      { name: 'Dimensions', description: 'Enter measurements' },
    ];
    if (productType === 'cover_and_foam') return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Foam', description: 'Select foam' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
    if (productType === 'cover_only') return [...base, { name: 'Fabric', description: 'Choose fabric' }, { name: 'Options', description: 'Add extras' }, { name: 'Review', description: 'Confirm order' }];
    return [...base, { name: 'Foam', description: 'Select foam' }, { name: 'Review', description: 'Confirm order' }];
  }, [productType]);

  const currentStepName = steps[currentStep]?.name || 'Product';

  const cmToUnit = useCallback((cm, u) => u === 'mm' ? cm * 10 : cm, []);
  const unitToCm = useCallback((value, u) => u === 'mm' ? value / 10 : value, []);

  const getDisplayValue = useCallback((field) => {
    if (inputValues[field] !== undefined && inputValues[field] !== '') return inputValues[field];
    const value = cmToUnit(dimensionsCm[field], unit);
    return value === 0 ? '' : value;
  }, [dimensionsCm, unit, cmToUnit, inputValues]);

  const handleDimensionInput = useCallback((field, rawValue) => {
    const filteredValue = rawValue.replace(/[^0-9.]/g, '');
    const parts = filteredValue.split('.');
    const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : filteredValue;
    setInputValues(prev => ({ ...prev, [field]: cleanValue }));
    if (cleanValue === '' || cleanValue === null) {
      setDimensionsCm(prev => ({ ...prev, [field]: 0 }));
    } else {
      const numValue = parseFloat(cleanValue);
      if (!isNaN(numValue)) setDimensionsCm(prev => ({ ...prev, [field]: unitToCm(numValue, unit) }));
    }
  }, [unit, unitToCm]);

  const handleDimensionBlur = useCallback((field) => {
    setInputValues(prev => ({ ...prev, [field]: '' }));
    setActiveField(null);
  }, []);

  const handleDimensionFocus = useCallback((field) => {
    const displayVal = cmToUnit(dimensionsCm[field], unit);
    setInputValues(prev => ({ ...prev, [field]: displayVal === 0 ? '' : String(displayVal) }));
    setActiveField(field);
  }, [dimensionsCm, unit, cmToUnit]);

  const getLimitInUnit = useCallback((field, limitType) => {
    const limits = {
      length: { min: cushion?.min_length || 20, max: cushion?.max_length || 100 },
      width: { min: cushion?.min_width || 20, max: cushion?.max_width || 100 },
      thickness: { min: cushion?.min_thickness || 2, max: cushion?.max_thickness || 15 },
      diameter: { min: 10, max: 60 },
    };
    return cmToUnit(limits[field]?.[limitType] || 0, unit);
  }, [cushion, unit, cmToUnit]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [cushionRes, fabricRangesRes] = await Promise.all([
          axios.get(`${API}/cushion-types/${cushionId}`),
          axios.get(`${API}/fabric-ranges`)
        ]);
        setCushion(cushionRes.data);
        setFabricRanges(fabricRangesRes.data);
      } catch (error) {
        toast.error('Failed to load product data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [cushionId]);

  const calculatePrice = useCallback(async () => {
    if (!cushion) return;
    const hasFabricSelection = (selectedFabricRange && selectedFabricColor) || selectedFabric;
    if (productType === 'cover_and_foam' && (!hasFabricSelection || !selectedFoam)) return;
    if (productType === 'cover_only' && !hasFabricSelection) return;
    if (productType === 'foam_only' && !selectedFoam) return;
    try {
      const response = await axios.post(`${API}/calculate-price`, {
        cushion_type_id: cushionId,
        dimensions: getDimensionsForApi(dimensionsCm),
        product_type: productType,
        fabric_id: selectedFabricRange ? null : (productType !== 'foam_only' ? selectedFabric : null),
        fabric_range_id: productType !== 'foam_only' ? selectedFabricRange : null,
        fabric_color_id: productType !== 'foam_only' ? selectedFabricColor : null,
        foam_type_id: productType !== 'cover_only' ? selectedFoam : null,
        quantity
      });
      setPriceInfo(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  }, [cushion, dimensionsCm, productType, selectedFabric, selectedFabricRange, selectedFabricColor, selectedFoam, quantity, cushionId, getDimensionsForApi]);

  useEffect(() => {
    const debounce = setTimeout(calculatePrice, 300);
    return () => clearTimeout(debounce);
  }, [calculatePrice]);

  const handleAddToCart = useCallback(async () => {
    if (!priceInfo) return;
    setAddingToCart(true);
    try {
      const cartItem = {
        cushion_type_id: cushionId,
        dimensions: getDimensionsForApi(dimensionsCm),
        product_type: productType,
        fabric_id: selectedFabricRange ? null : (productType !== 'foam_only' ? selectedFabric : null),
        fabric_range_id: productType !== 'foam_only' ? selectedFabricRange : null,
        fabric_color_id: productType !== 'foam_only' ? selectedFabricColor : null,
        foam_type_id: productType !== 'cover_only' ? selectedFoam : null,
        quantity,
        ...(getCartExtras ? getCartExtras() : {}),
      };
      const { addToCart } = await import('@/context/CartContext').then(m => ({ addToCart: null }));
      // Use the addToCart from the component that calls this hook
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  }, [priceInfo, cushionId, dimensionsCm, productType, selectedFabric, selectedFabricRange, selectedFabricColor, selectedFoam, quantity, getDimensionsForApi, getCartExtras]);

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      setTimeout(() => {
        const nextName = steps[next]?.name;
        if (nextName === 'Fabric' && stepContentRef.current && window.innerWidth < 1024) {
          const rect = stepContentRef.current.getBoundingClientRect();
          window.scrollTo({ top: window.scrollY + rect.top + 200, behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
    }
  }, [currentStep, steps]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    }
  }, [currentStep]);

  const selectedFoamObj = selectedFoam ? FOAM_DENSITIES[selectedFoam] : null;
  const selectedFabricRangeObj = useMemo(() => fabricRanges.find(r => r.id === selectedFabricRange), [fabricRanges, selectedFabricRange]);
  const selectedFabricColorObj = useMemo(() => selectedFabricRangeObj?.colors?.find(c => c.id === selectedFabricColor), [selectedFabricRangeObj, selectedFabricColor]);
  const displayFabricName = useMemo(() => (selectedFabricRangeObj && selectedFabricColorObj) ? `${selectedFabricRangeObj.name} - ${selectedFabricColorObj.name}` : '-', [selectedFabricRangeObj, selectedFabricColorObj]);
  const fabricColorForPreview = selectedFabricColorObj?.hex_color || null;
  const fabricImageUrl = useMemo(() => {
    const url = selectedFabricColorObj?.image_url;
    if (url?.includes('warwick.com.au') || url?.startsWith('/api/fabric-images')) return url;
    return null;
  }, [selectedFabricColorObj]);
  const pipingCost = useMemo(() => PIPING_OPTIONS.find(o => o.value === pipingOption)?.price || 0, [pipingOption]);

  return {
    // State
    currentStep, setCurrentStep, loading, addingToCart, productType, setProductType,
    activeTooltip, setActiveTooltip, activeField, setActiveField, stepContentRef,
    cushion, dimensionsCm, setDimensionsCm, inputValues, setInputValues, unit, setUnit,
    selectedFabric, setSelectedFabric, selectedFoam, setSelectedFoam,
    quantity, setQuantity, priceInfo,
    selectedFabricRange, setSelectedFabricRange, selectedFabricColor, setSelectedFabricColor,
    fabricZoom, setFabricZoom, fabricRanges,
    pipingOption, setPipingOption, pipingFabricRange, setPipingFabricRange,
    pipingFabricColor, setPipingFabricColor,
    galleryIndex, setGalleryIndex,
    // Computed
    steps, currentStepName, selectedFoamObj, selectedFabricRangeObj, selectedFabricColorObj,
    displayFabricName, fabricColorForPreview, fabricImageUrl, pipingCost,
    // Handlers
    cmToUnit, unitToCm, getDisplayValue, handleDimensionInput, handleDimensionBlur,
    handleDimensionFocus, getLimitInUnit, nextStep, prevStep,
    // Navigation helper
    navigate,
  };
};

export default useCustomizerFlow;
